import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { Avatar, AvatarFallback, AvatarImage } from "design-system/atoms/Avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Check, CircleAlert, ShieldAlert, TriangleAlert } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useSettingsWhatsappNumbersQuery } from "modules/settings/hooks/whatsapp/useSettingsWhatsappNumbersQuery"
import { useUpdateWhatsappNumberCompanyMutation } from "modules/settings/hooks/whatsapp/useUpdateWhatsappNumberCompanyMutation"
import { AdminOnly } from "modules/shared/components/AdminOnly"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { WhatsappNumberResponseDataSchema } from "schema"
import { z } from "zod"
import { WhatsappNumberVerify } from "./WhatsappNumberVerify"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof WhatsappNumberResponseDataSchema>
>()

export const WhatsappNumbersTable = () => {
  const { t } = useTranslation()
  const { data, isPending } = useSettingsWhatsappNumbersQuery()
  const { data: employee } = useAuthEmployeeQuery()

  const { mutateAsync: updateCompany, isPending: isUpdatingCompany } =
    useUpdateWhatsappNumberCompanyMutation()

  const organizationCompanies = useMemo(
    () => employee?.organization?.companies ?? [],
    [employee?.organization?.companies],
  )
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("settings.integrations.whatsapp.table.name"),
          cell: (props) => {
            const hasError = props.row.original.failedReason
            return (
              <div className="flex gap-1 items-center justify-between">
                <span>{props.getValue()}</span>
                {hasError && (
                  <Tooltip>
                    <TooltipTrigger>
                      <ShieldAlert size={16} className="text-destructive" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className="whitespace-pre-line">{props.row.original.failedReason}</span>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            )
          },
          meta: { colSpan: 3 },
        }),
        columnHelper.accessor("number", {
          header: t("settings.integrations.whatsapp.table.number"),
          meta: { colSpan: 3 },
          cell: (props) => (
            <div className="flex flex-col gap-1">
              <span>{props.getValue()}</span>
              {props.row.original.isOnBusinessApp && (
                <span className="text-muted-foreground">
                  {t("settings.integrations.whatsapp.coexistence.label")}
                </span>
              )}
            </div>
          ),
        }),
        columnHelper.accessor("numberId", {
          header: t("settings.integrations.whatsapp.table.numberId"),
          meta: { colSpan: 3 },
        }),
        columnHelper.accessor("companyId", {
          header: t("settings.integrations.whatsapp.table.company"),
          meta: { colSpan: 3 },
          cell: (props) => {
            const row = props.row.original
            const companyId = row.companyId
            const numberId = row.id

            if (organizationCompanies.length === 0) {
              return companyId ?? "—"
            }

            return (
              <Select
                value={companyId ?? ""}
                onValueChange={(companyId) => updateCompany({ id: numberId, companyId })}
                disabled={isUpdatingCompany || companyId != null}
              >
                <SelectTrigger className="h-8 w-full max-w-52">
                  <SelectValue
                    placeholder={t("settings.integrations.whatsapp.table.selectCompany")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {organizationCompanies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={c.logo ?? undefined} />
                            <AvatarFallback>{c.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>{c.name}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )
          },
        }),
        columnHelper.accessor("quality", {
          header: t("settings.integrations.whatsapp.table.quality"),
          meta: { colSpan: 3 },
          cell: (props) => {
            const quality = props.getValue()
            switch (quality) {
              case "GREEN":
                return <Check className="text-emerald-500" size={16} />
              case "YELLOW":
                return <TriangleAlert className="text-yellow-500" size={16} />
              case "RED":
                return <CircleAlert className="text-red-500" size={16} />
              default:
                return quality
            }
          },
        }),
        columnHelper.display({
          id: "coexistenceSync",
          header: t("settings.integrations.whatsapp.coexistence.sync"),
          cell: (props) => (
            <div className="flex flex-col gap-1">
              {props.row.original.coexistenceSyncs.map((sync) => (
                <Tooltip key={sync.type}>
                  <TooltipTrigger asChild>
                    <span tabIndex={sync.error ? 0 : undefined}>
                      {t("settings.integrations.whatsapp.coexistence.syncStatus", {
                        type: t(`settings.integrations.whatsapp.coexistence.types.${sync.type}`),
                        status: t(
                          `settings.integrations.whatsapp.coexistence.statuses.${sync.status}`,
                        ),
                        progress:
                          sync.progress === null
                            ? ""
                            : t("settings.integrations.whatsapp.coexistence.progress", {
                                value: sync.progress,
                              }),
                      })}
                    </span>
                  </TooltipTrigger>
                  {sync.error && <TooltipContent>{sync.error}</TooltipContent>}
                </Tooltip>
              ))}
            </div>
          ),
        }),
        columnHelper.display({
          header: "",
          id: "actions",
          meta: { align: "right" },
          cell: (props) => {
            const phoneId = props.row.original.numberId

            if (props.row.original.isOnBusinessApp) return null

            return (
              <AdminOnly>
                <WhatsappNumberVerify phoneId={phoneId} />
              </AdminOnly>
            )
          },
        }),
      ]),
    [t, organizationCompanies, updateCompany, isUpdatingCompany],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.integrations.whatsapp.table.noNumbers")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(cell.column.columnDef.meta?.className)}
            align={cell.column.columnDef.meta?.align}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
    </Table>
  )
}
